// cSpell: ignore externallink newtabexternallink
import { Inject, Injectable, SecurityContext } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { ApiService, RouteInfoService } from "@tylertech/core-module";
import { LOCATION, WINDOW } from "@tylertech/shared-module";
import { IIntent } from "@tylertech/tyler-api-types";
import { IntentDialogComponent, IntentDialogData } from "../components";
import { IntentHandler, IntentHandlerType, Resolver } from "../concrete";
import { getLoggerFor } from "@tylertech/logging";
import { DomSanitizer } from "@angular/platform-browser";

@Injectable()
export class IntentService
{
    private readonly log = getLoggerFor("IntentService");
    protected resolvers: Array<Resolver>;
    delayIntentResolve: boolean = false;
    intentToResolve: string = null;

    constructor(
        @Inject(WINDOW) private readonly window: Window,
        @Inject(LOCATION) private readonly location: Location,
        private readonly matDialog: MatDialog,
        private readonly $apiService: ApiService,
        private readonly $routeInfoService: RouteInfoService,
        private readonly domSanitizer: DomSanitizer
    )
    {
        this.resolvers = [];
        if (!this.resolvers)
        {
            this.resolvers = new Array<Resolver>();
        }

        this.registerFrontEndAction("Common", "mailto", "mailto",
            (data: Record<"mailto", string>) =>
            {
                window.open("mailto:" + data.mailto, "_blank");
                return Promise.resolve();
            });

        this.registerFrontEndAction("Common", "externallink", "externallink",
            (data: Record<"externallink", string>) =>
            {
                let address = data.externallink;
                if (address.indexOf("https://") === -1
                    && address.indexOf("http://") === -1)
                {
                    address = "https://" + address;
                }
                location.replace(address);
                return Promise.resolve();
            });

        this.registerFrontEndAction("Common", "newtabexternallink", "newtabexternallink",
            (data: Record<"newtabexternallink", string>) =>
            {
                let address = data.newtabexternallink;
                if (address.indexOf("https://") === -1 && address.indexOf("http://") === -1)
                {
                    address = "https://" + address;
                }
                window.open(address, "_blank");
                return Promise.resolve();
            });
    }

    private areEquivalent(main: Resolver, object: unknown): boolean
    {
        const res = object as Resolver;
        if (!res)
        {
            return false;
        }

        if (res.intent.MIME.toLowerCase() !== main.intent.MIME.toLowerCase()
            || res.intent.Action.toLowerCase() !== main.intent.Action.toLowerCase())
        {
            return false;
        }

        if (res.handler.Name !== main.handler.Name)
        {
            return false;
        }

        if (res.handler.URI !== main.handler.URI)
        {
            return false;
        }

        return true;
    }

    protected handle<T = unknown>(intent: IIntent, resolvers: Array<Resolver>): Promise<T>
    {
        if (resolvers.length === 1)
        {
            return this.navigate(resolvers[0], intent);
        }
        else if (resolvers.length > 1)
        {
            return this.handleMulti(intent, resolvers);
        }
        else
        {
            // no resolution found
            this.log.warning("no resolution found", { intent });
            return Promise.reject("IntentError: no resolution found");
        }
    }

    protected handleMulti<T = unknown>(intent: IIntent, resolvers: Array<Resolver>): Promise<T>
    {
        const dialogConfig: MatDialogConfig<IntentDialogData> = {
            data: {
                resolvers
            }
        };

        const dialogRef = this.matDialog.open(IntentDialogComponent, dialogConfig);
        return dialogRef.afterClosed()
            .toPromise()
            .then((res: Resolver) =>
            {
                return this.navigate<T>(res, intent);
            });
    }

    private isExternalUri(uri: string): boolean
    {
        return uri.indexOf("://") >= 0 || uri.indexOf("mailto:") >= 0;
    }

    protected navigate<T = unknown>(resolve: Resolver, intent: IIntent): Promise<T>
    {
        if (resolve.handler.HandlerType === IntentHandlerType.FrontEndAction)
        {
            return resolve.handler.Action(intent.Data) as Promise<T>;
        }

        let uri = resolve.handler.URI;
        const openNew = resolve.handler.OpenNew;
        const re: RegExp = /\{([^}]*)\}/g;
        const matches = uri.match(re);
        if (matches)
        {
            matches.forEach(element =>
            {
                const val = element.substring(1, element.length - 1);
                let replacementString = "";
                if (intent.Data && intent.Data[val])
                {
                    const index = uri.indexOf(element);
                    replacementString = this.needsEncoding(String(intent.Data[val]))
                        ? encodeURIComponent(intent.Data[val])
                        : String(intent.Data[val]);
                }

                uri = uri.replace(element, replacementString);
            });
        }

        uri = uri.replace(/ /g, "%20");

        let url: string;
        if (this.isExternalUri(uri))
        {
            url = this.domSanitizer.sanitize(SecurityContext.URL, uri);
        }
        else
        {
            const tenant = this.$routeInfoService.tenant;
            url = "/" + tenant + uri;
        }

        if (openNew)
        {
            this.window.open(url, "_blank");
        }
        else
        {
            if (this.delayIntentResolve && url.startsWith("incode://"))
                this.intentToResolve = url;
            else
                this.location.href = url;
        }

        return Promise.resolve(undefined);
    }

    private register(mime: string, action: string, name: string, uri: string): void
    {
        const handler: IntentHandler = {
            Name: name,
            URI: uri,
        };

        const intent: IIntent = {
            MIME: mime,
            Action: action,
        };

        this.registerResolver(
            new Resolver(intent, handler));
    }

    registerFrontEndAction<TData, TResult>(mime: string, action: string, name: string, actionFn: (data: TData) => Promise<TResult>): void
    {
        const handler: IntentHandler = {
            Name: name,
            HandlerType: IntentHandlerType.FrontEndAction,
            Action: actionFn,
        };

        const intent: IIntent = {
            MIME: mime,
            Action: action,
        };

        this.registerResolver(
            new Resolver(intent, handler));
    }

    protected registerIntent(intent: IIntent, name: string, uri: string, _icon: string = null, _color: string = null): void
    {
        const handler: IntentHandler = {
            Name: name,
            URI: uri,
        };
        this.registerResolver(new Resolver(intent, handler));
    }

    protected registerResolver(resolver: Resolver): void
    {
        this.resolvers = this.resolvers.filter(v => !this.areEquivalent(v, resolver));
        this.resolvers.push(resolver);
    }

    resolve<T = unknown>(mime: string, action: string, data: unknown): Promise<T>
    {
        const intent: IIntent = {
            MIME: mime,
            Action: action,
            Data: data,
        };
        return this.resolveIntent(intent);
    }

    /** @deprecated - this call disables the actual resolution of the Intent.. its only purpose would be to force a fetch of backend handlers... */
    resolveIntent<T = unknown>(intent: IIntent, resolve: false): Promise<T>;
    /** @deprecated - this is the default behavior, remove second argument  */
    resolveIntent<T = unknown>(intent: IIntent, resolve: true): Promise<T>;
    resolveIntent<T = unknown>(intent: IIntent): Promise<T>;
    resolveIntent<T = unknown>(intent: IIntent, resolve: boolean = true): Promise<T>
    {
        const resolvers = this.resolvers.filter(
            v =>
            {
                return v.intent.MIME.toLowerCase() === intent.MIME.toLowerCase() &&
                    v.intent.Action.toLowerCase() === intent.Action.toLowerCase();
            })
            .sort((a, b) =>
            {
                return -1 * (a.intent.Priority - b.intent.Priority);
            });

        if (!resolvers?.length)
        {
            const searchIntent: IIntent = {
                MIME: intent.MIME,
                Action: intent.Action,
            };
            return this.$apiService.sendAuth<IIntent, Array<IntentHandler>>(
                "IIntentService/GetIntentResolutions", searchIntent)
                .then(handlers =>
                {
                    if (!handlers)
                    {
                        return undefined;
                    }

                    handlers.forEach(handler =>
                    {
                        const res = new Resolver(searchIntent, handler);
                        resolvers.push(res);

                        this.registerResolver(res);
                    });
                    return resolvers;
                })
                .then((handlers: Array<Resolver> | undefined) =>
                {
                    if (resolve)
                    {
                        this.handle<T>(intent, resolvers);
                    }

                    // TODO - why are we returning the list of the backend handlers to the caller?
                    // TODO - and why are we casting to back to whatever they asked for?
                    return handlers as T;
                });
        }

        if (resolve)
        {
            return this.handle<T>(intent, resolvers);
        }

        return Promise.resolve(resolvers as T);
    }

    resolveURI<T = unknown>(uri: string): Promise<T>
    {
        if (uri.substring(0, 6).toLowerCase() !== "intent")
        {
            throw "Invalid Intent";
        }

        const [_, mime, action, dataStr] = uri.split("/", 4);

        let data = {};
        try
        {
            data = JSON.parse(decodeURIComponent(dataStr));
        }
        catch
        {
            /* Squashing error */
        }

        return this.resolve(mime, action, data);
    }

    private shouldEncodeUri(uri: string, index: number): boolean
    {
        if (this.isExternalUri(uri)
            || uri.startsWith("/")
            || uri.startsWith("?")
            || (index >= 0 && uri[index] === "/"))
        {
            return false;
        }
        else
        {
            return true;
        }
    }

    private needsEncoding(value: string): boolean
    {
        if (!value)
        {
            return false;
        }

        try
        {
            const decoded = decodeURIComponent(value);
            if (decoded !== value)
            {
                return false;
            }
        }
        catch
        {
            // Malformed encoding - treat as needing encoding
        }

        if (this.isExternalUri(value) || value.startsWith("/") || value.startsWith("?"))
        {
            return false;
        }

        return true;
    }
}
